import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { formatApiErrorDetail, API_BASE } from "@/lib/api";
import PasswordInput from "@/components/PasswordInput";
import MfaGate from "@/components/MfaGate";
import { homeFor } from "@/lib/home";

export default function Login() {
  const { user, login, applyUser } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);

  if (user && user !== false) {
    return <Navigate to={homeFor(user.role)} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email, password);
      if (u.mfa_required || u.mfa_setup_required) { setChallenge(u); return; }
      nav(homeFor(u.role));
    } catch (e) {
      setErr(
        (e.response?.data && formatApiErrorDetail(e.response.data.detail)) ||
        e.message ||
        "Invalid credentials"
      );
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1549909773-ab3c0eb3f73e?auto=format&fit=crop&w=1600&q=80"
          alt="Construction site" className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="flex items-center gap-4">
            <img src={`${API_BASE}/settings/company/logo`} alt="SOS" className="h-16 w-16 rounded-md object-contain bg-white/10 p-1" />
            <div>
              <div className="text-3xl font-black tracking-tight leading-tight">Safety Onsite<br/>Solutions, Inc.</div>
              <div className="text-xs uppercase tracking-[0.2em] text-green-400 mt-1">Operations Platform</div>
            </div>
          </div>
          <p className="mt-6 text-slate-200 max-w-md">The operations backbone for safety staffing —
            timesheets, approvals, invoices, ACH payouts. All in one.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 bg-white">
        {challenge ? (
          <MfaGate
            challenge={challenge}
            onSuccess={(usr) => { applyUser(usr); nav("/"); }}
            onCancel={() => { setChallenge(null); setPassword(""); }}
          />
        ) : (
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="mb-8">
            <div className="tinylabel">Sign in</div>
            <h1 className="text-3xl sm:text-4xl font-black mt-2">Welcome back</h1>
            <p className="text-slate-500 mt-2">Access the operations console or your assignments.</p>
          </div>
          <label className="block text-sm font-semibold text-slate-700">Email</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email" required autoComplete="email"
            className="mt-1 w-full h-12 px-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <label className="block text-sm font-semibold text-slate-700 mt-4">Password</label>
          <PasswordInput
            value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password" required autoComplete="current-password"
            wrapperClassName="mt-1"
            className="w-full h-12 px-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          {err && <div data-testid="login-error" className="mt-3 text-sm text-red-600">{err}</div>}
          <button
            type="submit" disabled={loading} data-testid="login-submit"
            className="mt-6 w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md transition-colors disabled:opacity-60"
          >{loading ? "Signing in..." : "Sign in"}</button>
          <div className="mt-3 text-right text-sm">
            <Link data-testid="forgot-link" to="/forgot-password" className="text-green-700 hover:text-green-800 font-semibold">Forgot password?</Link>
          </div>
          <Link to="/" className="hidden" />
        </form>
        )}
      </div>
    </div>
  );
}
